import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useAuth } from "@/composables/useAuth";

const routes: RouteRecordRaw[] = [
  // ---- Auth (unauthenticated) ----
  {
    path: "/login",
    name: "login",
    component: () => import("@/pages/auth/LoginView.vue"),
    meta: { title: "Sign in", guest: true },
  },

  // ---- App shell (everything behind auth) ----
  {
    path: "/",
    component: () => import("@/layouts/AppShell.vue"),
    meta: { requiresAuth: true },
    children: [
      { path: "", name: "home", redirect: { name: "sites" } },

      // --- Sites ---
      {
        path: "sites",
        name: "sites",
        component: () => import("@/pages/sites/SiteList.vue"),
        meta: { title: "Sites" },
      },
      {
        path: "sites/new",
        name: "site-new",
        component: () => import("@/pages/sites/NewSite.vue"),
        meta: { title: "New site" },
      },
      {
        path: "sites/:siteId",
        name: "site-overview",
        component: () => import("@/pages/sites/SiteOverview.vue"),
        props: true,
        meta: { title: "Overview" },
      },
      {
        path: "sites/:siteId/floor-plan",
        name: "site-floor-plan",
        component: () => import("@/pages/sites/FloorPlanEditor.vue"),
        props: true,
        meta: { title: "Floor plan" },
      },

      // --- Cameras ---
      {
        path: "sites/:siteId/cameras",
        name: "cameras",
        component: () => import("@/pages/cameras/CameraList.vue"),
        props: true,
        meta: { title: "Cameras" },
      },
      {
        path: "sites/:siteId/cameras/new",
        name: "camera-new",
        component: () => import("@/pages/cameras/CameraAddWizard.vue"),
        props: true,
        meta: { title: "Add camera", layout: "onboarding" },
      },
      {
        path: "sites/:siteId/cameras/:cameraId",
        name: "camera-detail",
        component: () => import("@/pages/cameras/CameraDetail.vue"),
        props: true,
        meta: { title: "Camera" },
      },

      // --- Zones ---
      {
        path: "sites/:siteId/zones",
        name: "zones",
        component: () => import("@/pages/zones/ZoneList.vue"),
        props: true,
        meta: { title: "Zones" },
      },

      // --- Analytics ---
      {
        path: "sites/:siteId/analytics",
        name: "analytics",
        component: () => import("@/pages/analytics/AnalyticsHub.vue"),
        props: true,
        meta: { title: "Analytics" },
      },

      // --- Alerts ---
      {
        path: "sites/:siteId/alerts",
        name: "alerts",
        component: () => import("@/pages/alerts/AlertRules.vue"),
        props: true,
        meta: { title: "Alert rules" },
      },
      {
        path: "sites/:siteId/alerts/events",
        name: "alert-events",
        component: () => import("@/pages/alerts/AlertEvents.vue"),
        props: true,
        meta: { title: "Alert events" },
      },

      // --- Stream health ---
      {
        path: "sites/:siteId/health",
        name: "stream-health",
        component: () => import("@/pages/health/StreamHealth.vue"),
        props: true,
        meta: { title: "Stream health" },
      },

      // --- Activity log ---
      {
        path: "sites/:siteId/activity",
        name: "activity",
        component: () => import("@/pages/activity/ActivityLog.vue"),
        props: true,
        meta: { title: "Activity" },
      },

      // --- Settings ---
      {
        path: "sites/:siteId/settings",
        name: "site-settings",
        component: () => import("@/pages/sites/SiteSettings.vue"),
        props: true,
        meta: { title: "Settings" },
      },
    ],
  },

  // ---- 404 ----
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/pages/NotFound.vue"),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, saved) {
    return saved ?? { top: 0 };
  },
});

router.beforeEach(async (to, _from, next) => {
  const auth = useAuth();

  if (!auth.initialized.value) {
    await auth.init();
  }

  const isAuth = auth.isAuthenticated.value;

  if (to.meta.guest && isAuth) {
    return next({ name: "sites" });
  }

  if (to.meta.requiresAuth && !isAuth) {
    return next({ name: "login", query: { redirect: to.fullPath } });
  }

  next();
});

router.afterEach((to) => {
  const title = (to.meta.title as string) || "WECAM";
  document.title = `${title} · WECAM`;
});
